import React, { useMemo, useState } from 'react';
import { MessageSquare, Download, Search, ArrowLeft, Calendar, Clock, Video, FileText, CreditCard, CheckCircle2, AlertTriangle, MoreVertical, Paperclip, User as UserIcon, ChevronRight, XCircle, RefreshCcw, Sparkles } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Pagination } from '../ui/Pagination';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface OrdersTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
  setActiveTab: (tab: string) => void;
}
type OrderStatus = 'new' | 'in_progress' | 'waiting_data' | 'waiting_consultation' | 'completed' | 'cancelled' | 'dispute' | 'refunded';
type PaymentStatus = 'paid' | 'pending' | 'refunded';
type OrderFormat = 'video' | 'audio' | 'chat' | 'text';
interface OrderHistoryItem {
  status: OrderStatus;
  date: string;
  description: string;
}
interface OrderFile {
  id: string;
  name: string;
  size: string;
  type: 'pdf' | 'image' | 'doc';
}
interface Order {
  id: string;
  serviceName: string;
  astrologerName: string;
  astrologerInitials: string;
  date: string;
  price: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  format: OrderFormat;
  deadline?: string;
  hasReport: boolean;
  hasConsultation: boolean;
  history: OrderHistoryItem[];
  files: OrderFile[];
  notes?: string;
}
const MOCK_ORDERS: Order[] = [{
  id: 'ORD-1042',
  serviceName: 'Консультация: Синастрия',
  astrologerName: 'Елена Смирнова',
  astrologerInitials: 'ЕС',
  date: '12 Окт 2024, 14:30',
  price: 5000,
  status: 'in_progress',
  paymentStatus: 'paid',
  format: 'video',
  deadline: '15 Окт 2024',
  hasReport: false,
  hasConsultation: true,
  history: [{
    status: 'new',
    date: '12 Окт 2024, 14:30',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '12 Окт 2024, 15:00',
    description: 'Астролог взял в работу'
  }],
  files: [],
  notes: 'Пожалуйста, подготовьте точное время рождения обоих партнеров.'
}, {
  id: 'ORD-0984',
  serviceName: 'Натальная карта (Полный разбор)',
  astrologerName: 'Анна Коваль',
  astrologerInitials: 'АК',
  date: '25 Сен 2024, 10:15',
  price: 3500,
  status: 'completed',
  paymentStatus: 'paid',
  format: 'text',
  hasReport: true,
  hasConsultation: false,
  history: [{
    status: 'new',
    date: '25 Сен 2024, 10:15',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '25 Сен 2024, 11:00',
    description: 'В работе'
  }, {
    status: 'completed',
    date: '28 Сен 2024, 16:45',
    description: 'Отчёт готов'
  }],
  files: [{
    id: 'f1',
    name: 'Натальная_карта_Анна.pdf',
    size: '2.4 MB',
    type: 'pdf'
  }]
}, {
  id: 'ORD-1055',
  serviceName: 'Соляр на 2025 год',
  astrologerName: 'Дмитрий Волков',
  astrologerInitials: 'ДВ',
  date: '14 Окт 2024, 09:00',
  price: 4000,
  status: 'waiting_data',
  paymentStatus: 'pending',
  format: 'text',
  hasReport: true,
  hasConsultation: false,
  history: [{
    status: 'new',
    date: '14 Окт 2024, 09:00',
    description: 'Заказ создан'
  }, {
    status: 'waiting_data',
    date: '14 Окт 2024, 09:05',
    description: 'Ожидание данных от клиента'
  }],
  files: [],
  notes: 'Необходимо заполнить анкету с местом встречи соляра.'
}, {
  id: 'ORD-1012',
  serviceName: 'Экспресс-ответ на вопрос',
  astrologerName: 'Мария Петрова',
  astrologerInitials: 'МП',
  date: '05 Окт 2024, 18:20',
  price: 1500,
  status: 'waiting_consultation',
  paymentStatus: 'paid',
  format: 'chat',
  deadline: '06 Окт 2024',
  hasReport: false,
  hasConsultation: true,
  history: [{
    status: 'new',
    date: '05 Окт 2024, 18:20',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '05 Окт 2024, 18:30',
    description: 'В работе'
  }, {
    status: 'waiting_consultation',
    date: '05 Окт 2024, 19:00',
    description: 'Ожидание начала чата'
  }],
  files: []
}, {
  id: 'ORD-0950',
  serviceName: 'Детский гороскоп',
  astrologerName: 'Елена Смирнова',
  astrologerInitials: 'ЕС',
  date: '10 Сен 2024, 11:00',
  price: 4500,
  status: 'cancelled',
  paymentStatus: 'refunded',
  format: 'text',
  hasReport: true,
  hasConsultation: false,
  history: [{
    status: 'new',
    date: '10 Сен 2024, 11:00',
    description: 'Заказ создан'
  }, {
    status: 'cancelled',
    date: '11 Сен 2024, 10:00',
    description: 'Отменен клиентом'
  }],
  files: []
}, {
  id: 'ORD-0921',
  serviceName: 'Бизнес-прогноз на месяц',
  astrologerName: 'Игорь Николаев',
  astrologerInitials: 'ИН',
  date: '01 Сен 2024, 15:45',
  price: 8000,
  status: 'dispute',
  paymentStatus: 'paid',
  format: 'video',
  hasReport: true,
  hasConsultation: true,
  history: [{
    status: 'new',
    date: '01 Сен 2024, 15:45',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '02 Сен 2024, 10:00',
    description: 'В работе'
  }, {
    status: 'completed',
    date: '05 Сен 2024, 14:00',
    description: 'Заказ сдан'
  }, {
    status: 'dispute',
    date: '06 Сен 2024, 09:30',
    description: 'Открыт спор'
  }],
  files: [{
    id: 'f2',
    name: 'Бизнес_прогноз_Сентябрь.pdf',
    size: '5.1 MB',
    type: 'pdf'
  }],
  notes: 'Клиент не согласен с результатами прогноза.'
}];
const STATUS_CONFIG: Record<OrderStatus, {
  label: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = {
  new: {
    label: 'Новый',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    icon: <Sparkles className="w-4 h-4" data-id="element-19195" />
  },
  in_progress: {
    label: 'В работе',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    icon: <RefreshCcw className="w-4 h-4" data-id="element-19196" />
  },
  waiting_data: {
    label: 'Ожидает данных',
    color: 'text-orange-700',
    bg: 'bg-orange-100',
    icon: <AlertTriangle className="w-4 h-4" data-id="element-19197" />
  },
  waiting_consultation: {
    label: 'Ожидает сессии',
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    icon: <Clock className="w-4 h-4" data-id="element-19198" />
  },
  completed: {
    label: 'Завершён',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    icon: <CheckCircle2 className="w-4 h-4" data-id="element-19199" />
  },
  cancelled: {
    label: 'Отменён',
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    icon: <XCircle className="w-4 h-4" data-id="element-19200" />
  },
  dispute: {
    label: 'Спор',
    color: 'text-rose-700',
    bg: 'bg-rose-100',
    icon: <AlertTriangle className="w-4 h-4" data-id="element-19201" />
  },
  refunded: {
    label: 'Возврат',
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    icon: <RefreshCcw className="w-4 h-4" data-id="element-19202" />
  }
};
const FORMAT_CONFIG: Record<OrderFormat, {
  label: string;
  icon: React.ReactNode;
}> = {
  video: {
    label: 'Видеозвонок',
    icon: <Video className="w-4 h-4" data-id="element-19203" />
  },
  audio: {
    label: 'Аудиозвонок',
    icon: <MessageSquare className="w-4 h-4" data-id="element-19204" />
  },
  chat: {
    label: 'Текстовый чат',
    icon: <MessageSquare className="w-4 h-4" data-id="element-19205" />
  },
  text: {
    label: 'Письменный отчёт',
    icon: <FileText className="w-4 h-4" data-id="element-19206" />
  }
};
type FilterType = 'all' | 'active' | 'completed' | 'cancelled' | 'with_report' | 'with_consultation';
const FILTERS: {
  id: FilterType;
  label: string;
}[] = [{
  id: 'all',
  label: 'Все заказы'
}, {
  id: 'active',
  label: 'Активные'
}, {
  id: 'completed',
  label: 'Завершенные'
}, {
  id: 'cancelled',
  label: 'Отмененные'
}, {
  id: 'with_report',
  label: 'С отчётом'
}, {
  id: 'with_consultation',
  label: 'С консультацией'
}];
const ITEMS_PER_PAGE = 5;
export function OrdersTab({
  theme,
  onNavigate,
  setActiveTab
}: OrdersTabProps) {
  const {
    t
  } = useLanguage();
  const {
    success,
    error
  } = useToast();
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  // Filtering and Search
  const filteredOrders = useMemo(() => {
    let result = MOCK_ORDERS;
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(o => o.id.toLowerCase().includes(query) || o.serviceName.toLowerCase().includes(query) || o.astrologerName.toLowerCase().includes(query));
    }
    // Filters
    switch (activeFilter) {
      case 'active':
        result = result.filter(o => ['new', 'in_progress', 'waiting_data', 'waiting_consultation'].includes(o.status));
        break;
      case 'completed':
        result = result.filter(o => o.status === 'completed');
        break;
      case 'cancelled':
        result = result.filter(o => ['cancelled', 'refunded'].includes(o.status));
        break;
      case 'with_report':
        result = result.filter(o => o.hasReport);
        break;
      case 'with_consultation':
        result = result.filter(o => o.hasConsultation);
        break;
    }
    return result;
  }, [searchQuery, activeFilter]);
  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Handlers
  const handleDownload = (fileName: string) => {
    success(t('userdash.orders.downloadStarted', {
      name: fileName
    }));
  };
  const handleCancelOrder = () => {
    success(t('userdash.orders.cancelRequested'));
  };
  const handleOpenDispute = () => {
    error(t('userdash.orders.disputeOpened'));
  };
  // Render Detail View
  if (selectedOrderId) {
    const order = MOCK_ORDERS.find(o => o.id === selectedOrderId);
    if (!order) return null;
    const statusConfig = STATUS_CONFIG[order.status];
    const formatConfig = FORMAT_CONFIG[order.format];
    return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-19207">
        {/* Detail Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-19208">
          <div className="flex items-center gap-4" data-id="element-19209">
            <Button variant="ghost" size="sm" className="p-2 -ml-2" onClick={() => setSelectedOrderId(null)} data-id="element-19210">
              <ArrowLeft className="w-5 h-5" data-id="element-19211" />
            </Button>
            <div data-id="element-19212">
              <div className="flex items-center gap-3" data-id="element-19213">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-19214">
                  {t('userdash.orders.orderNumber', {
                  id: order.id
                })}
                </h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${statusConfig.bg} ${statusConfig.color}`} data-id="element-19215">
                  {statusConfig.icon}
                  {statusConfig.label}
                </span>
              </div>
              <div className="text-sm text-slate-500 mt-1" data-id="element-19216">
                {t('userdash.orders.placedOn', {
                date: order.date
              })}
              </div>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-19217">
            <Button variant="outline" size="sm" icon={<MessageSquare className="w-4 h-4" data-id="element-19219" />} onClick={() => setActiveTab('chat')} data-id="element-19218">
              {t('userdash.orders.chatWithAstrologer')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-19220">
          {/* Main Content (Left Column) */}
          <div className="lg:col-span-2 space-y-6" data-id="element-19221">
            {/* Order Info Card */}
            <Card padding="lg" data-id="element-19222">
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-19223">
                {t('userdash.orders.orderInfo')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-19224">
                <div data-id="element-19225">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-19226">
                    {t('userdash.orders.service')}
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-19227">
                    {order.serviceName}
                  </div>
                </div>
                <div data-id="element-19228">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-19229">
                    {t('userdash.orders.format')}
                  </div>
                  <div className="font-medium text-slate-900 flex items-center gap-2" data-id="element-19230">
                    {formatConfig.icon}
                    {formatConfig.label}
                  </div>
                </div>
                <div data-id="element-19231">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-19232">
                    {t('userdash.orders.price')}
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-19233">
                    {order.price.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                {order.deadline && <div data-id="element-19234">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-19235">
                      {t('userdash.orders.deadline')}
                    </div>
                    <div className="font-medium text-slate-900 flex items-center gap-2" data-id="element-19236">
                      <Clock className="w-4 h-4 text-amber-500" data-id="element-19237" />
                      {order.deadline}
                    </div>
                  </div>}
              </div>

              {order.notes && <div className="mt-6 pt-6 border-t border-slate-100" data-id="element-19238">
                  <div className="text-sm text-slate-500 mb-2" data-id="element-19239">
                    {t('userdash.orders.notes')}
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl text-sm text-slate-700" data-id="element-19240">
                    {order.notes}
                  </div>
                </div>}
            </Card>

            {/* Astrologer Card */}
            <Card padding="md" className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-19241">
              <div className="flex items-center gap-4" data-id="element-19242">
                <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center font-bold text-lg`} data-id="element-19243">
                  {order.astrologerInitials}
                </div>
                <div data-id="element-19244">
                  <div className="text-sm text-slate-500" data-id="element-19245">
                    {t('userdash.orders.executor')}
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-19246">
                    {order.astrologerName}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => onNavigate('astrologer-profile')} data-id="element-19247">
                {t('userdash.orders.astrologerProfile')}
              </Button>
            </Card>

            {/* Files/Report Section */}
            {order.files.length > 0 && <Card padding="lg" data-id="element-19248">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-19249">
                  <Paperclip className="w-5 h-5 text-slate-400" data-id="element-19250" />
                  {t('userdash.orders.filesAndReports')}
                </h3>
                <div className="space-y-3" data-id="element-19251">
                  {order.files.map(file => <div key={file.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-xl hover:border-slate-200 transition-colors" data-id="element-19252">
                      <div className="flex items-center gap-3" data-id="element-19253">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-19254">
                          <FileText className="w-5 h-5" data-id="element-19255" />
                        </div>
                        <div data-id="element-19256">
                          <div className="font-medium text-slate-900 text-sm" data-id="element-19257">
                            {file.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-19258">
                            {file.size} • {file.type.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" icon={<Download className="w-4 h-4" data-id="element-19260" />} onClick={() => handleDownload(file.name)} data-id="element-19259">
                        {t('userdash.orders.download')}
                      </Button>
                    </div>)}
                </div>
              </Card>}
          </div>

          {/* Sidebar (Right Column) */}
          <div className="space-y-6" data-id="element-19261">
            {/* Status History */}
            <Card padding="lg" data-id="element-19262">
              <h3 className="text-lg font-bold text-slate-900 mb-6" data-id="element-19263">
                {t('userdash.orders.orderHistory')}
              </h3>
              <div className="relative pl-4 border-l-2 border-slate-100 space-y-6" data-id="element-19264">
                {order.history.map((item, index) => {
                const isLast = index === order.history.length - 1;
                const itemStatusConfig = STATUS_CONFIG[item.status];
                return <div key={index} className="relative" data-id="element-19265">
                      <div className={`absolute -left-[21px] w-3 h-3 rounded-full border-2 border-white ${isLast ? itemStatusConfig.bg.replace('bg-', 'bg-').replace('100', '500') : 'bg-slate-300'}`} data-id="element-19266" />
                      <div className="mb-1" data-id="element-19267">
                        <span className={`text-sm font-bold ${isLast ? 'text-slate-900' : 'text-slate-500'}`} data-id="element-19268">
                          {itemStatusConfig.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mb-1" data-id="element-19269">
                        {item.date}
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-19270">
                        {item.description}
                      </div>
                    </div>;
              })}
              </div>
            </Card>

            {/* Payment Info */}
            <Card padding="lg" data-id="element-19271">
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-19272">
                {t('userdash.orders.payment')}
              </h3>
              <div className="flex items-center justify-between mb-4" data-id="element-19273">
                <span className="text-slate-600" data-id="element-19274">
                  {t('userdash.orders.status')}
                </span>
                {order.paymentStatus === 'paid' ? <Badge variant="success" data-id="element-19275">
                    {t('userdash.orders.paymentPaid')}
                  </Badge> : order.paymentStatus === 'pending' ? <Badge variant="warning" data-id="element-19276">
                    {t('userdash.orders.paymentPending')}
                  </Badge> : <Badge variant="neutral" data-id="element-19277">
                    {t('userdash.orders.paymentRefunded')}
                  </Badge>}
              </div>
              <div className="flex items-center justify-between font-bold text-slate-900 pt-4 border-t border-slate-100" data-id="element-19278">
                <span data-id="element-19279">{t('userdash.orders.total')}</span>
                <span data-id="element-19280">{order.price.toLocaleString('ru-RU')} ₽</span>
              </div>
            </Card>

            {/* Actions */}
            <div className="space-y-3" data-id="element-19281">
              {['new', 'in_progress', 'waiting_data', 'waiting_consultation'].includes(order.status) && <Button variant="outline" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={handleCancelOrder} data-id="element-19282">
                  {t('userdash.orders.cancelOrder')}
                </Button>}
              {['completed'].includes(order.status) && <Button variant="ghost" fullWidth className="text-slate-500 hover:text-rose-600 hover:bg-rose-50" onClick={handleOpenDispute} data-id="element-19283">
                  {t('userdash.orders.openDispute')}
                </Button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Render List View
  return <div className="space-y-6" data-id="element-19284">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-19285">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-19286">
          {t('userdash.orders.title')}
        </h2>
        <Button onClick={() => onNavigate('astrologers')} data-id="element-19287">
          {t('userdash.orders.findAstrologer')}
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-19288">
        <div className="w-full md:w-72 flex-shrink-0" data-id="element-19289">
          <Input placeholder={t('userdash.orders.search')} value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }} icon={<Search className="w-5 h-5 text-slate-400" data-id="element-19291" />} data-id="element-19290" />
        </div>
        <div className="flex-1 overflow-x-auto pb-2 -mb-2 scrollbar-hide" data-id="element-19292">
          <div className="flex gap-2" data-id="element-19293">
            {FILTERS.map(filter => <button key={filter.id} onClick={() => {
            setActiveFilter(filter.id);
            setCurrentPage(1);
          }} className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeFilter === filter.id ? `${theme.bg} ${theme.text} ring-1 ${theme.ring}` : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-19294">
                {filter.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Orders List */}
      {paginatedOrders.length > 0 ? <div className="space-y-4" data-id="element-19295">
          {paginatedOrders.map(order => {
        const statusConfig = STATUS_CONFIG[order.status];
        const formatConfig = FORMAT_CONFIG[order.format];
        return <Card key={order.id} padding="md" className={`border-l-4 transition-all hover:shadow-md cursor-pointer ${order.status === 'completed' ? 'border-l-emerald-500' : order.status === 'cancelled' || order.status === 'refunded' ? 'border-l-slate-300 opacity-75' : order.status === 'dispute' ? 'border-l-rose-500' : `border-l-${theme.text.split('-')[1]}-500` // Use theme color for active
        }`} onClick={() => setSelectedOrderId(order.id)} data-id="element-19296">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-19297">
                  <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-19298">
                    <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center font-bold flex-shrink-0`} data-id="element-19299">
                      {order.astrologerInitials}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19300">
                      <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-19301">
                        <span className="text-sm font-mono text-slate-500" data-id="element-19302">
                          {order.id}
                        </span>
                        <span className="text-sm text-slate-400" data-id="element-19303">•</span>
                        <span className="text-sm text-slate-500" data-id="element-19304">
                          {order.date}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${statusConfig.bg} ${statusConfig.color}`} data-id="element-19305">
                          {statusConfig.label}
                        </span>
                        {order.paymentStatus === 'paid' && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100" data-id="element-19306">
                            {t('userdash.orders.paymentPaid')}
                          </span>}
                      </div>
                      <h3 className="font-bold text-slate-900 text-lg truncate mb-1" data-id="element-19307">
                        {order.serviceName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600" data-id="element-19308">
                        <span className="flex items-center gap-1" data-id="element-19309">
                          <UserIcon className="w-4 h-4" data-id="element-19310" />
                          {order.astrologerName}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-19311">
                          {formatConfig.icon}
                          {formatConfig.label}
                        </span>
                        <span className="font-medium text-slate-900" data-id="element-19312">
                          {order.price.toLocaleString('ru-RU')} ₽
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto mt-2 md:mt-0 pt-4 md:pt-0 border-t md:border-t-0 border-slate-100" data-id="element-19313">
                    {order.hasReport && order.status === 'completed' && <Button variant="outline" size="sm" className="w-full sm:w-auto" icon={<Download className="w-4 h-4" data-id="element-19315" />} onClick={e => {
                e.stopPropagation();
                handleDownload('Отчет.pdf');
              }} data-id="element-19314">
                        {t('userdash.orders.download')}
                      </Button>}
                    <Button variant="outline" size="sm" className="w-full sm:w-auto" icon={<MessageSquare className="w-4 h-4" data-id="element-19317" />} onClick={e => {
                e.stopPropagation();
                setActiveTab('chat');
              }} data-id="element-19316">
                      {t('userdash.orders.chat')}
                    </Button>
                    <Button variant="ghost" size="sm" className="w-full sm:w-auto text-slate-500 hover:text-slate-900" icon={<ChevronRight className="w-4 h-4" data-id="element-19319" />} data-id="element-19318">
                      {t('userdash.orders.details')}
                    </Button>
                  </div>
                </div>
              </Card>;
      })}
        </div> : <Card padding="xl" className="flex flex-col items-center justify-center text-center" data-id="element-19320">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4" data-id="element-19321">
            <Search className="w-8 h-8 text-slate-400" data-id="element-19322" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-19323">
            {t('userdash.orders.noOrders')}
          </h3>
          <p className="text-slate-500 max-w-md mb-6" data-id="element-19324">
            {t('userdash.orders.noOrdersDesc')}
          </p>
          <Button variant="outline" onClick={() => {
        setSearchQuery('');
        setActiveFilter('all');
      }} data-id="element-19325">
            {t('userdash.orders.resetFilters')}
          </Button>
        </Card>}

      {/* Pagination */}
      {totalPages > 1 && <div className="mt-8 flex justify-center" data-id="element-19326">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-19327" />
        </div>}
    </div>;
}